import { distinctUntilChanged, filter, map } from 'rxjs';
import { store$ } from '@redux/store';
import { ResultType } from 'features/searchResultsFacets';
import LoadingStatus from 'enums/loadingStatus';
import {
  isAnyHousingTypeActiveSelector,
  selectBrandId,
  selectGarages,
  selectLivingAreas,
  selectPrimaryBedroom,
  selectRvGarage,
  selectSchools,
  selectStories,
  activeAdultCommunitiesSelector,
  selectPriceReduction,
  selectReviews,
  selectConsumerPromos,
  selectNatureAreas,
  selectPool,
  selectGolfCourse,
  selectGreen,
  selectSportsFacilities,
  selectParks,
  selectViews,
  selectWaterfront,
  selectGatedCommunity,
  selectMarketingStatus,
  selectCountsAndResultType,
  selectHomeOwnersAssociationFee,
} from '../redux/selectors';
import OtherFiltersType from '../enums/otherFiltersType';

let appliedFilters = [];
const label = document.querySelector('[data-advanced-panel]');
const labelTxt = document.querySelector('[data-advanced-panel] span');
const initialLabelText = labelTxt.textContent;
const buttonConfirmation = document.querySelector('[data-panel-allfilters] [data-apply]');

/**
 * A function that is called when the counts/result type [community] change to update the confirmation button's text.
 */

function setButtonTextForCommunity(listingCounts) {
  buttonConfirmation.textContent = `Show ${listingCounts?.toLocaleString()} communities`;
}

/**
 * A function that is called when the counts/result type [homes] change to update the confirmation button's text.
 */

function setButtonTextForHomes(listingCounts) {
  buttonConfirmation.textContent = `Show ${listingCounts?.toLocaleString()} homes`;
}

const isResultTypeCommunity = (resultType) => resultType === ResultType.Communities.toString();

const isResultTypeLocations = (resultType) => resultType === ResultType.Locations.toString();
/**
 * When the counts or result type change, map the the selector to get the result type
 * and the listing counts information.
 * Skip the first time for inital load.
 */
const onCountsAndResultsTypeChange$ = store$.pipe(
  map(selectCountsAndResultType),
  filter(
    ({ resultCountsStatus }) =>
      resultCountsStatus?.name === LoadingStatus.Succeeded.toString() ||
      resultCountsStatus?.name === LoadingStatus.Idle.toString()
  ),
  distinctUntilChanged()
);

onCountsAndResultsTypeChange$
  .pipe(
    filter(({ resultType }) => isResultTypeCommunity(resultType) || isResultTypeLocations(resultType)),
    map(({ listingCounts }) => listingCounts)
  )
  .subscribe(setButtonTextForCommunity);

onCountsAndResultsTypeChange$
  .pipe(
    filter(({ resultType }) => !isResultTypeCommunity(resultType) && !isResultTypeLocations(resultType)),
    map(({ listingCounts }) => listingCounts)
  )
  .subscribe(setButtonTextForHomes);

/**
 * Emits a custom event when the advanced filters panel is clicked,
 * passing the current applied filters as the event detail.
 */
function handleAdvancedPanelClick() {
  // emit a custom event that lets subscribers know that the panel has been clicked
  const event = new CustomEvent('advancedFiltersClicked');

  document.body.dispatchEvent(event);
}

label.addEventListener('click', handleAdvancedPanelClick);

/**
 * A function that is called when the filter is changed to update the advanced label text.
 */
function updateLabel(data) {
  const { value, name } = data;
  if (!value || value === '0' || value.length === 0) {
    // remove the filter from the applied filters if the value is 0
    appliedFilters = appliedFilters.filter((element) => element !== name);
  }
  // only add the applied filter to the list if not already in the list
  else if (appliedFilters.indexOf(name) < 0) {
    appliedFilters.push(name);
  }

  if (appliedFilters.length > 0) {
    if (appliedFilters.length === 1) {
      labelTxt.textContent = `${appliedFilters.length} Filter`;
    } else {
      labelTxt.textContent = `${appliedFilters.length} Filters`;
    }
    label.classList.add('nhs-c-select-button--active');
  } else if (appliedFilters.length === 0) {
    labelTxt.textContent = initialLabelText;
    label.classList.remove('nhs-c-select-button--active');
  }
}

/**
 * Subscribing to the store when living areas change to update the label.
 */
store$
  .pipe(
    map(selectLivingAreas),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'livingArea' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when stories change to update the label.
 */
store$
  .pipe(
    map(selectStories),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'stories' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when privary bedroom change to update the label.
 */
store$
  .pipe(
    map(selectPrimaryBedroom),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'primaryBedroom' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when garages change to update the label.
 */
store$
  .pipe(
    map(selectGarages),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'garages' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when nature areas change to update the label.
 */
store$
  .pipe(
    map(selectNatureAreas),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'natureAreas' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when pool change to update the label.
 */
store$
  .pipe(
    map(selectPool),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'pool' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when golf course change to update the label.
 */
store$
  .pipe(
    map(selectGolfCourse),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'golfCourse' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when green change to update the label.
 */
store$
  .pipe(
    map(selectGreen),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'green' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when parks change to update the label.
 */
store$
  .pipe(
    map(selectParks),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'parks' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when views change to update the label.
 */
store$
  .pipe(
    map(selectViews),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'views' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when water front change to update the label.
 */
store$
  .pipe(
    map(selectWaterfront),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'waterfront' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when gated community change to update the label.
 */
store$
  .pipe(
    map(selectGatedCommunity),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'gatedCommunity' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when sports facilities change to update the label.
 */
store$
  .pipe(
    map(selectSportsFacilities),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'sportsFacilities' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when housing planel change to update the label.
 */
store$
  .pipe(
    map(isAnyHousingTypeActiveSelector),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'housingPanel' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when builder change to update the label.
 */
store$
  .pipe(
    map(selectBrandId),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'builder' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when RV garages change to update the label.
 */
store$
  .pipe(
    map(selectRvGarage),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'rvGarages' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when schools change to update the label.
 */
store$
  .pipe(
    map(selectSchools),
    distinctUntilChanged(),
    map((schools) => ({ value: schools?.length, name: 'schools' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when hot deals change to update the label.
 */
store$
  .pipe(
    map(selectConsumerPromos),
    distinctUntilChanged(),
    map((value) => ({ value, name: OtherFiltersType.HotDeals.toString() }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when reviews change to update the label.
 */
store$
  .pipe(
    map(selectReviews),
    distinctUntilChanged(),
    map((value) => ({ value, name: OtherFiltersType.Reviews.toString() }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when price reduction change to update the label.
 */
store$
  .pipe(
    map(selectPriceReduction),
    distinctUntilChanged(),
    map((value) => ({ value, name: OtherFiltersType.PriceReduction.toString() }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when community status change to update the label.
 */
store$
  .pipe(
    map(selectMarketingStatus),
    distinctUntilChanged(),
    map((marketingStatus) => ({ value: marketingStatus?.length }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when the active adult filters change to update the label.
 */
store$
  .pipe(
    map(activeAdultCommunitiesSelector),
    distinctUntilChanged(),
    // check that the "show 55+ communities" or the "exclude 55+ communities" are applied.
    map((value) => value.only || value.exclude),
    map((value) => ({ value, name: 'activeAdult' }))
  )
  .subscribe(updateLabel);

/**
 * Subscribing to the store when hasHomeOwnersAssociationFee change to update the label.
 */
store$
  .pipe(
    map(selectHomeOwnersAssociationFee),
    distinctUntilChanged(),
    map((value) => ({ value, name: 'hoa' }))
  )
  .subscribe(updateLabel);

require('./housingTypeSelect');
require('./livingAreas');
require('./stories');
require('./primaryBedroom');
require('./garages');
require('./communityAmenities');
require('./builders');
require('./rvGarage');
require('./schools');
require('./communityStatus');
require('./otherFilters');
require('./adultCommunities');
